import React, { useMemo, useState } from "react";
import { Button, Card, Table, Typography, message } from "antd";
import moment from "moment";
import { useGetOrgsQuery } from "../../store/auth/orgapi";
import { useGetFinalTokensQuery } from "../../store/is/finaltokapi";
import { DisplayOrganization } from "../Utils/DisplayOrganization";
import ViewDownloadCertificate from "./ViewDownloadCertificate";
import ViewRetirementDownloadCertificate from "./ViewRetirementDownloadCertificate";

function resolveReadableProjectId(record) {
  const rawCertificateJson = record?.final_certificate_json;
  if (!rawCertificateJson) {
    return "-";
  }

  let certificateDetails = rawCertificateJson;
  if (typeof rawCertificateJson === "string") {
    try {
      certificateDetails = JSON.parse(rawCertificateJson);
    } catch (error) {
      return "-";
    }
  }

  const certificate = Array.isArray(certificateDetails)
    ? certificateDetails[0]
    : Array.isArray(certificateDetails?.data)
      ? certificateDetails.data[0]
      : certificateDetails;

  const projectId = certificate?.intervention_project_id;
  return projectId ? String(projectId) : "-";
}

function AdminCertificateView() {
  const [selectedEntryData, setSelectedEntryData] = useState(null);
  const [isShowCertificate, setIsShowCertificate] = useState(false);
  const [isShowRetiredCertificate, setIsShowRetiredCertificate] = useState(false);

  const { data: finalTokenData, isLoading: isFinalTokenDataLoading } =
    useGetFinalTokensQuery();
  const { data: orgDetails } = useGetOrgsQuery();

  const organizationOptions = useMemo(() => {
    const orgList = orgDetails?.orgList;
    if (!Array.isArray(orgList)) {
      return [];
    }

    return orgList
      .filter((org) => org?._id && org?.name)
      .map((org) => ({
        value: org._id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [orgDetails]);

  const tableTokenData = useMemo(() => {
    if (!Array.isArray(finalTokenData)) {
      return [];
    }

    return finalTokenData.filter((record) => record && record.no_of_token !== 0);
  }, [finalTokenData]);

  const projectIdOptions = useMemo(() => {
    const uniqueProjectIds = new Set(
      tableTokenData
        .map((record) => resolveReadableProjectId(record))
        .filter((projectId) => projectId && projectId !== "-")
    );

    return Array.from(uniqueProjectIds)
      .sort((a, b) => a.localeCompare(b))
      .map((projectId) => ({
        text: projectId,
        value: projectId,
      }));
  }, [tableTokenData]);

  const closeCertificateModal = () => {
    setIsShowCertificate(false);
    setIsShowRetiredCertificate(false);
    setSelectedEntryData(null);
  };

  const handleOpenCertificate = (record) => {
    if (!record?._id || !record?.final_certificate_json) {
      message.error("Certificate data is missing for this record.");
      return;
    }

    setSelectedEntryData(record);
    if (record.status === "retired") {
      setIsShowCertificate(false);
      setIsShowRetiredCertificate(true);
      return;
    }

    setIsShowRetiredCertificate(false);
    setIsShowCertificate(true);
  };

  const columns = [
    {
      key: "type",
      title: "Type",
      dataIndex: "type",
      sorter: (a, b) => (a?.type || "").localeCompare(b?.type || ""),
      render: (value) => (
        <Typography.Text style={{ textTransform: "capitalize" }}>
          {value}
        </Typography.Text>
      ),
    },
    {
      key: "ptcb",
      title: "Token Id",
      dataIndex: "finaltoken_id",
      sorter: (a, b) => Number(a?.finaltoken_id || 0) - Number(b?.finaltoken_id || 0),
      render: (value) => <Typography.Text>PTCB-{value}</Typography.Text>,
    },
    {
      key: "ptc_count",
      title: "Number of Credits",
      dataIndex: "no_of_token",
      sorter: (a, b) => Number(a?.no_of_token || 0) - Number(b?.no_of_token || 0),
    },
    {
      key: "project_id",
      title: "Project ID",
      filters: projectIdOptions,
      onFilter: (value, record) => resolveReadableProjectId(record) === value,
      render: (record) => (
        <Typography.Text>{resolveReadableProjectId(record)}</Typography.Text>
      ),
      sorter: (a, b) =>
        resolveReadableProjectId(a).localeCompare(resolveReadableProjectId(b)),
    },
    {
      key: "owner",
      title: "Owner",
      dataIndex: "organization_id",
      filters: organizationOptions.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      onFilter: (value, record) => record?.organization_id === value,
      sorter: (a, b) => (a?.organization_id || "").localeCompare(b?.organization_id || ""),
      render: (organizationId) => (
        <DisplayOrganization orgid={organizationId} place="table" />
      ),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      sorter: (a, b) => (a?.status || "").localeCompare(b?.status || ""),
      render: (value) => (
        <Typography.Text style={{ textTransform: "capitalize" }}>
          {value}
        </Typography.Text>
      ),
    },
    {
      key: "created",
      title: "Created At",
      dataIndex: "created",
      sorter: (a, b) => {
        const timestampA = a?.created?.["$date"] || a?.created || 0;
        const timestampB = b?.created?.["$date"] || b?.created || 0;
        return Number(timestampA) - Number(timestampB);
      },
      render: (value) => {
        const timestamp = value?.["$date"] || value;
        return timestamp
          ? moment(parseInt(timestamp, 10)).local().format("DD MMM, YYYY @ hh:mm:ss")
          : undefined;
      },
    },
    {
      key: "actions",
      title: "Actions",
      render: (record) => {
        const buttonLabel =
          record?.status === "retired" ? "View Proof of Retirement" : "View Certificate";

        return (
          <Button type="primary" onClick={() => handleOpenCertificate(record)}>
            {buttonLabel}
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ height: "80vh" }}>
      <Typography.Title level={2}>All Certificates</Typography.Title>
      <Card className="shadow-card" style={{ borderRadius: 8 }}>
        <Table
          columns={columns}
          dataSource={tableTokenData}
          loading={isFinalTokenDataLoading}
          rowKey={(record) => record._id}
          pagination={{
            pageSizeOptions: ["10", "50", "100"],
            showSizeChanger: true,
          }}
        />
      </Card>

      {tableTokenData.map((tokenData) => {
        if (tokenData?._id !== selectedEntryData?._id) {
          return null;
        }

        if (isShowRetiredCertificate) {
          return (
            <ViewRetirementDownloadCertificate
              key={tokenData?._id}
              finalTokenData={tokenData}
              isShowRetiredCertificate={isShowRetiredCertificate}
              yieldRecord={tokenData?.final_certificate_json}
              closeModal={closeCertificateModal}
            />
          );
        }

        if (isShowCertificate) {
          return (
            <ViewDownloadCertificate
              key={tokenData?._id}
              finalTokenData={tokenData}
              isShowCertificate={isShowCertificate}
              yieldRecord={tokenData?.final_certificate_json}
              closeModal={closeCertificateModal}
            />
          );
        }

        return null;
      })}
    </div>
  );
}

export default AdminCertificateView;
